import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Spin } from 'antd';
import 'pdfjs-dist/legacy/build/pdf.worker.entry';
import { getDocument } from 'pdfjs-dist/legacy/build/pdf';

const PdfPreview = ({ pdfData, pageSettings, renderPageOverlay }) => {
  const wrapRef = useRef(null);
  const wrapWidthRafRef = useRef(0);
  const lastWrapWidthRef = useRef(0);
  const [wrapWidth, setWrapWidth] = useState(900);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [pageCssSizes, setPageCssSizes] = useState([]);

  useEffect(() => {
    // 监听容器宽度变化，用于自适应计算渲染 scale
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries?.[0]?.contentRect?.width;
      const next = Number(w || 0);
      if (!Number.isFinite(next) || next <= 0) return;
      if (Math.abs(next - Number(lastWrapWidthRef.current || 0)) < 2) return;
      lastWrapWidthRef.current = next;
      if (wrapWidthRafRef.current) cancelAnimationFrame(wrapWidthRafRef.current);
      wrapWidthRafRef.current = requestAnimationFrame(() => setWrapWidth(next));
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (wrapWidthRafRef.current) cancelAnimationFrame(wrapWidthRafRef.current);
      wrapWidthRafRef.current = 0;
    };
  }, []);

  useEffect(() => {
    // 加载 PDF 文档对象（pdfjs）
    if (!pdfData) {
      setPdfDoc(null);
      setPageCount(0);
      setPageCssSizes([]);
      setErrorText('');
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setErrorText('');
    const bytes = pdfData instanceof ArrayBuffer ? new Uint8Array(pdfData) : pdfData;
    const task = getDocument({ data: bytes });
    task.promise
      .then((doc) => {
        if (!alive) return;
        setPdfDoc(doc);
        setPageCount(Number(doc?.numPages || 0));
      })
      .catch(() => {
        if (!alive) return;
        setPdfDoc(null);
        setPageCount(0);
        setPageCssSizes([]);
        setErrorText('PDF加载失败');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      // 组件卸载/数据变化时销毁任务，避免内存泄漏与旧回调写入
      alive = false;
      try { task.destroy(); } catch (e) {}
    };
  }, [pdfData]);

  const canvasRefs = useMemo(() => {
    return Array.from({ length: pageCount }, () => React.createRef());
  }, [pageCount]);

  useEffect(() => {
    if (!pdfDoc || pageCount <= 0) return undefined;
    let alive = true;
    let renderTasks = [];

    (async () => {
      try {
        // 以第一页 viewport 计算一个适配容器宽度的 scale
        const firstPage = await pdfDoc.getPage(1);
        if (!alive) return;
        const baseViewport = firstPage.getViewport({ scale: 1 });
        const desiredWidth = Math.max(360, Math.min(1000, Number(wrapWidth || 900)));
        const nextScale = baseViewport?.width ? (desiredWidth / baseViewport.width) : 1;

        // 使用 devicePixelRatio 提升清晰度，但用 CSS 宽高保持布局稳定
        const dpr = Math.max(1, Number(window.devicePixelRatio || 1));
        const firstViewport = firstPage.getViewport({ scale: nextScale * dpr });
        const cssW = firstViewport?.width ? (firstViewport.width / dpr) : 0;
        const cssH = firstViewport?.height ? (firstViewport.height / dpr) : 0;
        if (cssW > 0 && cssH > 0) {
          setPageCssSizes(Array.from({ length: pageCount }, () => ({ widthPx: cssW, heightPx: cssH })));
        }
        const nextSizes = [];
        for (let i = 1; i <= pageCount; i += 1) {
          const page = await pdfDoc.getPage(i);
          if (!alive) return;
          const viewport = page.getViewport({ scale: nextScale * dpr });
          const canvas = canvasRefs[i - 1]?.current;
          if (!canvas) continue;
          const ctx = canvas.getContext('2d');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = `${viewport.width / dpr}px`;
          canvas.style.height = `${viewport.height / dpr}px`;
          const rt = page.render({ canvasContext: ctx, viewport });
          renderTasks.push(rt);
          await rt.promise;
          nextSizes.push({ widthPx: viewport.width / dpr, heightPx: viewport.height / dpr });
        }
        if (!alive) return;
        setPageCssSizes(nextSizes);
      } catch (e) {
        if (!alive) return;
        setErrorText('PDF渲染失败');
      }
    })();

    return () => {
      alive = false;
      // 中断未完成的渲染任务，避免并发渲染与状态错写
      renderTasks.forEach((t) => {
        try { t.cancel(); } catch (e) {}
      });
      renderTasks = [];
    };
  }, [pdfDoc, pageCount, wrapWidth, canvasRefs]);

  if (!pdfData) return null;

  const ps = pageSettings || { widthMm: 210, heightMm: 297, marginTopMm: 25, marginRightMm: 25, marginBottomMm: 25, marginLeftMm: 25 };

  return (
    <div ref={wrapRef} style={{ width: '100%' }}>
      {errorText ? <Alert type="warning" showIcon message={errorText} style={{ marginBottom: 12 }} /> : null}
      {loading ? (
        <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
          {Array.from({ length: pageCount }).map((_, idx) => {
            const sz = pageCssSizes[idx] || null;
            const cssW = Number(sz?.widthPx || 0);
            const cssH = Number(sz?.heightPx || 0);
            return (
              <div
                key={idx}
                style={{
                  width: cssW ? `${cssW}px` : 'auto',
                  height: cssH ? `${cssH}px` : 'auto',
                  background: '#fff',
                  border: '1px solid #ddd',
                  boxShadow: '0 2px 6px rgba(0,0,0,.06)',
                  position: 'relative',
                }}
              >
                <canvas ref={canvasRefs[idx]} />
                {typeof renderPageOverlay === 'function' && cssW && cssH
                  ? (
                    <div style={{ position: 'absolute', inset: 0 }}>
                      {renderPageOverlay({ pageIndex: idx, pageCount, pageSettings: ps, cssWidthPx: cssW, cssHeightPx: cssH })}
                    </div>
                  )
                  : null}
                <div style={{ position: 'absolute', right: 12, bottom: 10, fontSize: 12, color: '#999' }}>{idx + 1}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PdfPreview;
